import SaveIcon from '@mui/icons-material/Save';
import { Alert, Button, Snackbar } from '@mui/material';
import { useParams } from 'react-router-dom';

import { useDisclosure } from '@hooks/useDisclosure';

import { useSaveEsgGreenhouse } from '@Domains/Esg/EsgGreenhouse';
import { mapEsgGreenhouseToFormValues } from '@Router/EsgGreenhouse/utils';
import { findUserRoles } from '@Utils/roleUtils';
import { updateCompletedSection } from '@Utils/updateCompletedSection';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';
import { useGreenhouseForm } from '../EsgGreenhouseFormProvider';
import { GREENHOUSE_STANDARD_LIST_ID } from '../GreenhouseStandard/GreenhouseStandardList';
import { otherFieldsInteracted } from '../GreenhouseStandard/validators';
import { mapEsgGreenhouseToPayload } from './utils';
import {
  deleteAssets,
  IUploadImage,
  useDeleteImage,
} from '@Domains/UploadImage';

const EsgGreenhouseSaveButton = () => {
  const { id = '' } = useParams();

  const { trigger, getValues, reset, setValue } = useGreenhouseForm();
  const [{ loading, error }, saveEsgGreenhouse] = useSaveEsgGreenhouse(id);
  const { refetchProgressWithUpdate, isPublished } = useProgressBar();

  const [openedSnackbar, { open: openSnackbar, close: closeSnackbar }] =
    useDisclosure();

  const { user } = useProfile();

  const { deleteImage } = useDeleteImage(Number(id));

  const formValues = getValues();

  const handleSave = async () => {
    const principles = formValues.greenhouseStandard?.principles || {};
    const otherPrinciple =
      principles[`principle${GREENHOUSE_STANDARD_LIST_ID.OTHER}`];

    if (otherPrinciple?.checked) {
      otherFieldsInteracted.nameTh = true;
      otherFieldsInteracted.nameEn = true;
    }

    if (user) {
      const { cvupCreatorRole, cvupApproverRole } = findUserRoles(user.N_ROLES);

      setValue(
        'greenhouseStandard.completedSection',
        updateCompletedSection(
          formValues.greenhouseStandard?.completedSection,
          cvupCreatorRole ? { N_ROLE: cvupCreatorRole } : undefined,
          cvupApproverRole ? { N_ROLE: cvupApproverRole } : undefined
        )
      );

      setValue(
        'greenhouseGoalTerm.completedSection',
        updateCompletedSection(
          formValues.greenhouseGoalTerm?.completedSection,
          cvupCreatorRole ? { N_ROLE: cvupCreatorRole } : undefined,
          cvupApproverRole ? { N_ROLE: cvupApproverRole } : undefined
        )
      );

      setValue(
        'greenhouseStrategy.completedSection',
        updateCompletedSection(
          formValues.greenhouseStrategy?.completedSection,
          cvupCreatorRole ? { N_ROLE: cvupCreatorRole } : undefined,
          cvupApproverRole ? { N_ROLE: cvupApproverRole } : undefined
        )
      );

      setValue(
        'climateChangeStructure.completedSection',
        updateCompletedSection(
          formValues.climateChangeStructure?.completedSection,
          cvupCreatorRole ? { N_ROLE: cvupCreatorRole } : undefined,
          cvupApproverRole ? { N_ROLE: cvupApproverRole } : undefined
        )
      );

      const updatedPerformanceSection = updateCompletedSection(
        formValues.performanceGhGasMgmtReport?.completedSection,
        cvupCreatorRole ? { N_ROLE: cvupCreatorRole } : undefined,
        cvupApproverRole ? { N_ROLE: cvupApproverRole } : undefined
      );
      if (updatedPerformanceSection) {
        setValue(
          'performanceGhGasMgmtReport.completedSection',
          updatedPerformanceSection
        );
      }

      setValue(
        'completedSection',
        updateCompletedSection(
          formValues.completedSection,
          cvupCreatorRole ? { N_ROLE: cvupCreatorRole } : undefined,
          cvupApproverRole ? { N_ROLE: cvupApproverRole } : undefined
        )
      );
      const performanceGhGasMgmtAssetsToDelete =
        formValues.performanceGhGasMgmtReport?.performanceGhGasMgmt?.assets?.filter(
          (asset: IUploadImage) => asset.markedForDeletion
        ) || [];

      if (performanceGhGasMgmtAssetsToDelete.length) {
        deleteAssets(
          'performanceGhGasMgmtReportPerformanceGhGasMgmt',
          performanceGhGasMgmtAssetsToDelete,
          deleteImage
        );
      }

      const updatedFormValues = getValues();
      const payload = mapEsgGreenhouseToPayload(updatedFormValues);

      try {
        const response = await saveEsgGreenhouse({ data: payload });

        if (response) {
          const dataFormValues = mapEsgGreenhouseToFormValues(
            response.data,
            updatedFormValues
          );
          reset({ ...dataFormValues });
        }
      } catch (error) {
        console.error(error);
      } finally {
        refetchProgressWithUpdate(true);
        trigger();
        openSnackbar();
      }
    }
  };

  return (
    <>
      <Button
        type="submit"
        color="inherit"
        startIcon={<SaveIcon />}
        onClick={handleSave}
        loading={loading}
        disabled={isPublished}
      >
        บันทึก
      </Button>
      <Snackbar
        open={openedSnackbar}
        autoHideDuration={2000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={!!error ? 'error' : 'success'}
          onClose={closeSnackbar}
          sx={{
            alignItems: 'center',
            marginBottom: 6,
            '.MuiAlert-icon': { py: 1 },
            '.MuiAlert-message': { py: 1 },
          }}
        >
          {!!error ? 'เกิดข้อผิดพลาดในการบันทึก' : 'บันทึกสําเร็จ'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EsgGreenhouseSaveButton;
